<template>
    <div class="bo-cart-list" :style="{ '--toggle-primary': primaryColor }">
        <div class="bo-cart-list__header">
            <div class="bo-cart-list__header-left">
                <div class="bo-cart-list__header-left-wrapper">
                    <div
                        class="indicator bo-cart-list__indicator"
                        data-toggle="collapse"
                        data-target="#bo-cart-list-items"
                    >
                        <i class="icon-expandmore"></i>
                    </div>
                    <input
                        type="checkbox"
                        class="bo-cart-list__header-checkbox"
                        :checked="selectedAllItems"
                        :indeterminate.prop="isIndeterminate"
                        @click="selectAllItems"
                    />
                    <div class="bo-cart-list__title-wrap">
                        <span class="bo-cart-list__title">
                            {{ cartTranslations.cart || "Cart" }} ({{ normalisedItems.length }})
                        </span>
                    </div>
                    <slot name="left_header_buttons"></slot>
                </div>
            </div>

            <div class="bo-cart-list__header-right">
                <div
                    v-if="showViewToggle"
                    class="bo-cart-list__toggle bo-cart-list__toggle--list-view"
                >
                    <label class="bo-cart-list__toggle-switch">
                        <input
                            type="checkbox"
                            class="bo-cart-list__toggle-input toggle-view"
                            :checked="viewMode === 'list'"
                            @change="emitToggleView"
                        />
                        <span class="bo-cart-list__toggle-slider bo-cart-list__toggle-slider--round"></span>
                        <div class="bo-cart-list__toggle-icons">
                            <i
                                class="bo-cart-list__toggle-icon bo-cart-list__toggle-icon--default icon-densitycompact"
                            ></i>
                            <i class="bo-cart-list__toggle-icon bo-cart-list__toggle-icon--list icon-listview"></i>
                        </div>
                    </label>
                </div>
            </div>
        </div>

        <div
            class="collapse show bo-cart-list__body"
            id="bo-cart-list-items"
        >
            <div
                v-for="item in normalisedItems"
                :key="item.id"
            >
                <div
                    class="bo-cart-list__row"
                    :class="{
                        'bo-cart-list__row--clickable': isCompactLayout,
                        'bo-cart-list__row--no-stock': hasStockWarning(item),
                    }"
                    @click="onRowClick(item, $event)"
                >
                    <div
                        class="bo-cart-list__row-left"
                        :class="{ 'bo-cart-list__row-left--has-more': shouldShowMoreButton(item) }"
                    >
                        <input
                            type="checkbox"
                            :value="item.id"
                            v-model="selectedIds"
                            class="bo-cart-list__checkbox"
                        />

                        <img
                            :src="resolveImage(item)"
                            alt="product image"
                            class="bo-cart-list__image"
                        />

                        <button
                            type="button"
                            class="bo-cart-list__icon-action bo-cart-list__icon-action--mobile bo-cart-list__icon-action--mobile-under-image"
                            @click="openEditModal(item)"
                        >
                            <i class="icon-edit"></i>
                        </button>

                        <div class="bo-cart-list__basic-info">
                            <div class="bo-cart-list__info-stack">
                                <div class="bo-cart-list__meta">
                                    <span
                                        v-if="canReadPrices && item.has_volume_pricing"
                                        class="bo-cart-list__volume-pricing"
                                    >
                                        {{ cartTranslations.volume_pricing || "Volume Pricing" }}
                                    </span>
                                    <span class="bo-cart-list__sku">
                                        {{ getSku(item) }}
                                    </span>
                                    <span
                                        v-if="showStockBadge(item)"
                                        class="bo-cart-list__stock"
                                        :class="getStockClass(item.product_id)"
                                    >
                                        {{ getStockText(item) }}
                                    </span>
                                    <span
                                        v-if="item.product.allow_backorders"
                                        class="bo-cart-list__backorder"
                                    >
                                        {{ cartTranslations.backorder || "Backorder" }}
                                    </span>
                                    <span
                                        v-if="hasStockWarning(item)"
                                        class="bo-cart-list__stock-warning"
                                    >
                                        {{ getStockWarningText(item) }}
                                    </span>
                                </div>

                                <div class="bo-cart-list__name">{{ item.product?.name || "-" }}</div>

                                <div
                                    v-if="getSecondaryMetaEntries(item).length"
                                    class="bo-cart-list__meta-secondary"
                                >
                                    <span
                                        v-for="(metaEntry, index) in getSecondaryMetaEntries(item)"
                                        :key="`${item.id}-meta-entry-${index}`"
                                        class="bo-cart-list__meta-secondary-entry"
                                    >
                                        <span
                                            v-if="index > 0"
                                            class="bo-cart-list__meta-secondary-item--separator"
                                            >|</span
                                        >
                                        <span class="bo-cart-list__meta-secondary-item">
                                            {{ metaEntry }}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <button
                                v-if="shouldShowMoreButton(item)"
                                type="button"
                                class="bo-cart-list__btn bo-cart-list__btn--more bo-cart-list__btn--more-desktop"
                                :class="{ 'bo-cart-list__btn--expanded': expandedItems[item.id] }"
                                @click="toggleExpanded(item.id)"
                            >
                                <i class="icon-chevronright" /> More
                            </button>
                        </div>
                    </div>

                    <div class="bo-cart-list__row-right">
                        <!-- Price Section -->
                        <div
                            v-if="canReadPrices"
                            class="bo-cart-list__price-section"
                        >
                            <div class="bo-cart-list__price-head">
                                <div class="bo-cart-list__price-main">
                                    <div class="bo-cart-list__unit-price">
                                        <span
                                            class="bo-cart-list__currency"
                                            :class="{ 'bo-cart-list__currency--discounted': shouldShowDiscount(item) }"
                                        >
                                            {{ currencySymbol }}
                                        </span>
                                        <span
                                            class="bo-cart-list__price-final"
                                            :class="{ 'bo-cart-list__price-final--discounted': shouldShowDiscount(item) }"
                                        >
                                            {{ formatPrice(getEffectiveUnitPrice(item)) }}
                                        </span>
                                        <span
                                            v-if="hasTaxInfo(item)"
                                            class="bo-cart-list__tax"
                                        >
                                            {{ getTaxDisplay(item) }}
                                        </span>
                                    </div>
                                </div>

                                <bo-point
                                    v-if="isPointEnabled()"
                                    class="bo-cart-list__point bo-cart-list__point--inline-mobile"
                                    :value="calculatePoint(getEffectiveUnitPrice(item))"
                                ></bo-point>
                            </div>
                            <div
                                v-if="shouldShowOriginalPrice(item)"
                                class="bo-cart-list__discount-row"
                            >
                                <span class="bo-cart-list__price-original">
                                    {{ currencySymbol }} {{ formatPrice(getBaseUnitPrice(item)) }}
                                </span>
                                <bo-line-discount
                                    class="bo-cart-list__discount"
                                    show-all
                                    :steps="getLineDiscountSteps(item)"
                                ></bo-line-discount>
                            </div>
                            <bo-point
                                v-if="isPointEnabled()"
                                class="bo-cart-list__point bo-cart-list__point--stacked"
                                :value="calculatePoint(getEffectiveUnitPrice(item))"
                            ></bo-point>
                        </div>

                        <div class="bo-cart-list__row-counter">
                            <bo-product-quantity-uom-counter
                                :product="item.product"
                                :quantity="parseFloat(item.quantity) || 1"
                                :decimal-place="quantityDecimalPlace"
                                @change_quantity="emitQty(item, $event)"
                                @change_uom="emitUom(item, $event)"
                            ></bo-product-quantity-uom-counter>
                        </div>

                        <div
                            v-if="canReadPrices"
                            class="bo-cart-list__total-price bo-cart-list__total-price--row"
                        >
                            <div class="bo-cart-list__currency">{{ currencySymbol }}</div>
                            <div class="bo-cart-list__price-final">{{ formatPrice(getItemTotal(item)) }}</div>
                        </div>

                        <button
                            type="button"
                            class="bo-cart-list__icon-action bo-cart-list__icon-action--desktop"
                            @click="openEditModal(item)"
                        >
                            <i class="icon-edit"></i>
                        </button>
                    </div>

                    <div class="bo-cart-list__mobile-summary">
                        <button
                            v-if="shouldShowMoreButton(item)"
                            type="button"
                            class="bo-cart-list__btn bo-cart-list__btn--more bo-cart-list__btn--more-mobile"
                            :class="{ 'bo-cart-list__btn--expanded': expandedItems[item.id] }"
                            @click="toggleExpanded(item.id)"
                        >
                            <i class="icon-chevronright" /> More
                        </button>

                        <div class="bo-cart-list__mobile-summary-metrics">
                            <div class="bo-cart-list__mobile-summary-metric bo-cart-list__mobile-summary-metric--order">
                                <span class="bo-cart-list__mobile-summary-label">{{
                                    cartTranslations.order || "Order"
                                }}</span>
                                <span class="bo-cart-list__mobile-summary-value">{{ getOrderDisplay(item) }}</span>
                            </div>
                            <div
                                v-if="canReadPrices"
                                class="bo-cart-list__mobile-summary-metric bo-cart-list__mobile-summary-metric--total"
                            >
                                <span class="bo-cart-list__mobile-summary-label">{{
                                    cartTranslations.total || "Total"
                                }}</span>
                                <span class="bo-cart-list__mobile-summary-value"
                                    >{{ currencySymbol }} {{ formatPrice(getItemTotal(item)) }}</span
                                >
                            </div>
                        </div>
                    </div>

                    <transition name="bo-cart-list__details-transition">
                        <!-- Expanded Details Section -->
                        <div
                            v-if="expandedItems[item.id] && shouldShowMoreButton(item)"
                            :key="item.id + '-details'"
                            class="bo-cart-list__details bo-cart-list__details--inline"
                        >
                            <div
                                class="bo-cart-list__details-grid"
                                :class="{
                                    'bo-cart-list__details-grid--single': !(
                                        item.product?.type === 'bundle' && item.product?.components
                                    ),
                                }"
                            >
                                <!-- Bundle Components -->
                                <div
                                    v-if="item.product?.type === 'bundle' && item.product?.components"
                                    class="bo-cart-list__details-col bo-cart-list__details-col--components"
                                >
                                    <div class="bo-cart-list__details-section bo-cart-list__details-section--components">
                                        <div class="bo-cart-list__details-title">
                                            {{ cartTranslations.components || "Components" }}
                                        </div>
                                        <table class="bo-cart-list__bundle-table">
                                            <tbody>
                                                <tr
                                                    v-for="(component, idx) in getComponentRows(item)"
                                                    :key="idx"
                                                >
                                                    <td class="bo-cart-list__bundle-col bo-cart-list__bundle-col--name">
                                                        {{ component.name }}
                                                    </td>
                                                    <td class="bo-cart-list__bundle-col bo-cart-list__bundle-col--qty">
                                                        <span class="bo-cart-list__bundle-qty">{{
                                                            component.quantity
                                                        }}</span>
                                                        <span class="bo-cart-list__bundle-uom">{{ component.uom }}</span>
                                                    </td>
                                                    <td
                                                        v-if="canReadPrices"
                                                        class="bo-cart-list__bundle-col bo-cart-list__bundle-col--price"
                                                    >
                                                        {{ currencySymbol }}&nbsp;{{ formatPrice(component.total) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div class="bo-cart-list__details-col bo-cart-list__details-col--meta">
                                    <div class="bo-cart-list__details-meta">
                                        <div class="bo-cart-list__details-meta-row">
                                            <span class="bo-cart-list__details-meta-label">Lead Time (Day)</span>
                                            <span class="bo-cart-list__details-meta-value">{{
                                                getLeadTimeDisplay(item)
                                            }}</span>
                                        </div>
                                        <div class="bo-cart-list__details-meta-row">
                                            <span class="bo-cart-list__details-meta-label">{{
                                                cartTranslations.remark || "Remark"
                                            }}</span>
                                            <span class="bo-cart-list__details-meta-value">{{ item.remark || "-" }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <button
                        type="button"
                        class="bo-cart-list__icon-action bo-cart-list__icon-action--mobile bo-cart-list__icon-action--mobile-delete"
                        @click="openDeleteModal(item)"
                    >
                        <i class="icon-deleteforever"></i>
                    </button>
                </div>
            </div>

            <div
                v-if="normalisedItems.length === 0"
                class="bo-cart-list__empty"
            >
                <bo-svg
                    :svg-content="'/assets/graphics/img-no-data.svg'"
                    :primary-color="primaryColor"
                    class="bo-cart-list__empty-image"
                ></bo-svg>
                <div class="bo-cart-list__empty-title">
                    {{ generalTranslations.no_results_found || "No results found" }}
                </div>
            </div>
        </div>

        <div
            v-if="selectedItemIds.length"
            class="bo-cart-list__bulk-actions"
        >
            <button
                type="button"
                class="btn-rounded danger clear-selection bo-cart-list__btn bo-cart-list__btn--bulk-remove"
                @click="emitRemove(selectedItemIds)"
            >
                <i class="icon-deleteforever"></i>
                {{
                    Object.keys(generalTranslations).length === 0
                        ? "Clear Item"
                        : generalTranslations?.clear_object?.replace(":object", generalTranslations?.item ?? "Item") ??
                          "Clear Item"
                }}
            </button>
        </div>

        <bo-popup-modal
            :active="editModalActive"
            :title="editModalTitle"
            :action="generalTranslations.confirm || 'Confirm'"
            :modal-style="'modal-lg has-table bo-cart-list-edit-modal'"
            @deactivate="closeEditModal"
            @trigger="saveEditedItem"
        >
            <template #body-content>
                <div
                    v-if="editItem"
                    class="bo-cart-list__edit-modal"
                >
                    <div class="bo-cart-list__edit-top">
                        <img
                            :src="resolveImage(editItem)"
                            alt="product image"
                            class="bo-cart-list__edit-image"
                        />

                        <div class="bo-cart-list__edit-summary">
                            <div class="bo-cart-list__meta bo-cart-list__meta--edit">
                                <span
                                    v-if="canReadPrices && editItem.has_volume_pricing"
                                    class="bo-cart-list__volume-pricing"
                                >
                                    {{ cartTranslations.volume_pricing || "Volume Pricing" }}
                                </span>
                                <span class="bo-cart-list__sku bo-cart-list__sku--edit">{{ getSku(editItem) }}</span>
                                <span
                                    v-if="showStockBadge(editItem)"
                                    class="bo-cart-list__stock bo-cart-list__stock--edit"
                                    :class="getStockClass(editItem.product_id)"
                                >
                                    {{ getStockText(editItem) }}
                                </span>
                                <span
                                    v-if="editItem.product.allow_backorders"
                                    class="bo-cart-list__backorder bo-cart-list__backorder--edit"
                                >
                                    {{ cartTranslations.backorder || "Backorder" }}
                                </span>
                            </div>

                            <div class="bo-cart-list__name">{{ editItem.product?.name || "-" }}</div>

                            <div
                                v-if="getSecondaryMetaEntries(editItem).length"
                                class="bo-cart-list__meta-secondary"
                            >
                                <span
                                    v-for="(metaEntry, index) in getSecondaryMetaEntries(editItem)"
                                    :key="`${editItem.id}-meta-entry-${index}`"
                                    class="bo-cart-list__meta-secondary-entry"
                                >
                                    <span
                                        v-if="index > 0"
                                        class="bo-cart-list__meta-secondary-item--separator"
                                        >|</span
                                    >
                                    <span class="bo-cart-list__meta-secondary-item">
                                        {{ metaEntry }}
                                    </span>
                                </span>
                            </div>

                            <div
                                v-if="canReadPrices"
                                class="bo-cart-list__price-section bo-cart-list__edit-price bo-cart-list__edit-price--mobile"
                            >
                                <div class="bo-cart-list__unit-price">
                                    <span
                                        class="bo-cart-list__currency"
                                        :class="{
                                            'bo-cart-list__currency--discounted': shouldShowDiscount(editItem),
                                        }"
                                    >
                                        {{ currencySymbol }}
                                    </span>
                                    <span
                                        class="bo-cart-list__price-final"
                                        :class="{
                                            'bo-cart-list__price-final--discounted': shouldShowDiscount(editItem),
                                        }"
                                    >
                                        {{ formatPrice(getEffectiveUnitPrice(editItem)) }}
                                    </span>
                                    <span
                                        v-if="hasTaxInfo(editItem)"
                                        class="bo-cart-list__tax"
                                    >
                                        {{ getTaxDisplay(editItem) }}
                                    </span>
                                    <bo-point
                                        v-if="isPointEnabled()"
                                        class="bo-cart-list__point bo-cart-list__point--inline-mobile"
                                        :value="calculatePoint(getEffectiveUnitPrice(editItem))"
                                    ></bo-point>
                                </div>
                                <div
                                    v-if="shouldShowOriginalPrice(editItem)"
                                    class="bo-cart-list__discount-row"
                                >
                                    <span class="bo-cart-list__price-original">
                                        {{ currencySymbol }} {{ formatPrice(getBaseUnitPrice(editItem)) }}
                                    </span>
                                    <bo-line-discount
                                        class="bo-cart-list__discount"
                                        show-all
                                        :steps="getLineDiscountSteps(editItem)"
                                    ></bo-line-discount>
                                </div>
                                <bo-point
                                    v-if="isPointEnabled()"
                                    class="bo-cart-list__point bo-cart-list__point--stacked"
                                    :value="calculatePoint(getEffectiveUnitPrice(editItem))"
                                ></bo-point>
                            </div>
                        </div>

                        <div class="bo-cart-list__edit-actions">
                            <div
                                v-if="canReadPrices"
                                class="bo-cart-list__price-section bo-cart-list__edit-price bo-cart-list__edit-price--desktop"
                            >
                                <div class="bo-cart-list__unit-price">
                                    <span
                                        class="bo-cart-list__currency"
                                        :class="{
                                            'bo-cart-list__currency--discounted': shouldShowDiscount(editItem),
                                        }"
                                    >
                                        {{ currencySymbol }}
                                    </span>
                                    <span
                                        class="bo-cart-list__price-final"
                                        :class="{
                                            'bo-cart-list__price-final--discounted': shouldShowDiscount(editItem),
                                        }"
                                    >
                                        {{ formatPrice(getEffectiveUnitPrice(editItem)) }}
                                    </span>
                                    <span
                                        v-if="hasTaxInfo(editItem)"
                                        class="bo-cart-list__tax"
                                    >
                                        {{ getTaxDisplay(editItem) }}
                                    </span>
                                    <bo-point
                                        v-if="isPointEnabled()"
                                        class="bo-cart-list__point bo-cart-list__point--inline-mobile"
                                        :value="calculatePoint(getEffectiveUnitPrice(editItem))"
                                    ></bo-point>
                                </div>
                                <div
                                    v-if="shouldShowOriginalPrice(editItem)"
                                    class="bo-cart-list__discount-row"
                                >
                                    <span class="bo-cart-list__price-original">
                                        {{ currencySymbol }} {{ formatPrice(getBaseUnitPrice(editItem)) }}
                                    </span>
                                    <bo-line-discount
                                        class="bo-cart-list__discount"
                                        show-all
                                        :steps="getLineDiscountSteps(editItem)"
                                    ></bo-line-discount>
                                </div>
                                <bo-point
                                    v-if="isPointEnabled()"
                                    class="bo-cart-list__point bo-cart-list__point--stacked"
                                    :value="calculatePoint(getEffectiveUnitPrice(editItem))"
                                ></bo-point>
                            </div>

                            <bo-product-quantity-uom-counter
                                class="bo-cart-list__edit-counter bo-cart-list__edit-counter--desktop"
                                :product="editProduct"
                                :quantity="editDraft.quantity"
                                :decimal-place="quantityDecimalPlace"
                                @change_quantity="onEditQuantityChange"
                                @change_uom="onEditUomChange"
                            ></bo-product-quantity-uom-counter>

                            <div
                                v-if="canReadPrices"
                                class="bo-cart-list__total-price bo-cart-list__edit-total bo-cart-list__edit-total--desktop"
                            >
                                <div class="bo-cart-list__currency">{{ currencySymbol }}</div>
                                <div class="bo-cart-list__price-final">{{ editDraftTotal }}</div>
                            </div>
                        </div>

                        <div
                            v-if="
                                editItem.product?.type === 'bundle' &&
                                getComponentRows(editItem, editDraft.quantity).length > 0
                            "
                            class="bo-cart-list__edit-components"
                        >
                            <div class="bo-cart-list__edit-components-title">
                                {{ cartTranslations.components || "Components" }}
                            </div>
                            <table class="bo-cart-list__bundle-table bo-cart-list__bundle-table--edit">
                                <tbody>
                                    <tr
                                        v-for="(component, idx) in getComponentRows(editItem, editDraft.quantity)"
                                        :key="`edit-component-${idx}`"
                                    >
                                        <td class="bo-cart-list__bundle-col bo-cart-list__bundle-col--name">
                                            {{ component.name }}
                                        </td>
                                        <td class="bo-cart-list__bundle-col bo-cart-list__bundle-col--qty">
                                            <span class="bo-cart-list__bundle-qty">{{ component.quantity }}</span>
                                            <span class="bo-cart-list__bundle-uom">{{ component.uom }}</span>
                                        </td>
                                        <td
                                            v-if="canReadPrices"
                                            class="bo-cart-list__bundle-col bo-cart-list__bundle-col--price"
                                        >
                                            {{ currencySymbol }} {{ formatPrice(component.total) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="bo-cart-list__edit-divider"></div>

                    <div class="bo-cart-list__edit-bottom">
                        <div class="bo-cart-list__edit-mobile-order bo-cart-list__edit-mobile-order--mobile">
                            <label class="bo-cart-list__edit-label bo-cart-list__edit-label--mobile-order">{{
                                cartTranslations.order || "Order"
                            }}</label>

                            <div class="bo-cart-list__edit-mobile-controls bo-cart-list__edit-mobile-controls--mobile">
                                <bo-product-quantity-uom-counter
                                    class="bo-cart-list__edit-counter bo-cart-list__edit-counter--mobile"
                                    :product="editProduct"
                                    :quantity="editDraft.quantity"
                                    :decimal-place="quantityDecimalPlace"
                                    @change_quantity="onEditQuantityChange"
                                    @change_uom="onEditUomChange"
                                ></bo-product-quantity-uom-counter>

                                <div
                                    v-if="canReadPrices"
                                    class="bo-cart-list__total-price bo-cart-list__edit-total bo-cart-list__edit-total--mobile"
                                >
                                    <div class="bo-cart-list__currency">{{ currencySymbol }}</div>
                                    <div class="bo-cart-list__price-final">{{ editDraftTotal }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="bo-cart-list__edit-field">
                            <label class="bo-cart-list__edit-label">{{
                                cartTranslations.lead_time || "Lead Time (Day)"
                            }}</label>
                            <input
                                class="bo-cart-list__edit-input"
                                type="text"
                                :value="getLeadTimeDisplay(editItem)"
                                disabled
                            />
                        </div>

                        <div class="bo-cart-list__edit-field bo-cart-list__edit-field--remark">
                            <label class="bo-cart-list__edit-label">{{ cartTranslations.remark || "Remark" }}</label>
                            <textarea
                                v-model="editDraft.remark"
                                class="bo-cart-list__edit-textarea"
                                :placeholder="cartTranslations.add_message_here || 'Add a message here'"
                                rows="4"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </template>

            <template #side-info>
                <div
                    v-if="editItem && canReadPrices"
                    class="bo-cart-list__edit-footer-total"
                >
                    <span class="bo-cart-list__edit-footer-total-label">{{ cartTranslations.total || "Total" }}</span>
                    <span class="bo-cart-list__edit-footer-total-value">{{ currencySymbol }} {{ editDraftTotal }}</span>
                </div>
            </template>
        </bo-popup-modal>
    </div>
</template>

<script>
import boPoint from "js/frontend/components/bo-point.vue";
import boProductQuantityUomCounter from "js/frontend/components/bo-product-quantity-uom-counter";
import { formatPrice as formatPriceUtil } from "@/v2/utils/utilities.js";
import boLineDiscount from "js/frontend/components/bo-line-discount.vue";
import { cartItemDiscountSteps } from "js/frontend/utils/line-discount-label.js";

export default {
    name: "bo-cart-list",
    components: { boProductQuantityUomCounter, boPoint, boLineDiscount },

    props: {
        items: {
            type: Array,
            default: () => [],
        },
        // Direct-web catalog price gate (read_prices + the Hide Prices setting). Defaults
        // open so a consumer that does not pass it keeps rendering prices.
        canReadPrices: {
            type: Boolean,
            default: true,
        },
        catalogUrl: {
            type: String,
            default: "",
        },

        userWarehouseInventories: {
            type: Object,
            default: () => ({}),
        },
        // Same map the table view's bo-datatable gets: an item id present as a key means
        // the line is short of stock, so both views flag the same rows.
        rowWarningOptions: {
            type: Object,
            default: () => ({}),
        },

        pointConfig: {
            type: [Object, Boolean],
            default: null,
        },
        weightUnit: {
            type: String,
            default: "",
        },

        hasInventory: {
            type: Boolean,
            default: false,
        },
        hasCapabilityPhysical: {
            type: Boolean,
            default: false,
        },
        hasCapabilitySellable: {
            type: Boolean,
            default: false,
        },
        hasCapabilityRoughSellable: {
            type: Boolean,
            default: false,
        },
        roughStockText: {
            type: String,
            default: "",
        },

        taxIncluded: {
            type: Boolean,
            default: false,
        },
        displayPricesDuringCartAndCheckoutIncludingTax: {
            type: Boolean,
            default: false,
        },
        taxExempted: {
            type: Boolean,
            default: false,
        },
        siteCurrencyDecimalPlaces: {
            type: Number,
            default: 2,
        },
        hasCouponDiscount: {
            type: Boolean,
            default: false,
        },

        generalTranslations: {
            type: Object,
            default: () => window.bo?.generalTranslations || {},
        },
        cartTranslations: {
            type: Object,
            default: () => window.bo?.cartTranslations || {},
        },
        currencySymbol: {
            type: String,
            default: () => window.bo?.currencySymbol || "RM",
        },
        primaryColor: {
            type: String,
            default: () => window.bo_primary_color || '#594c45',
        },

        defaultImage: {
            type: String,
            default: "/assets/graphics/img-product.svg",
        },

        viewMode: {
            type: String,
            default: "list",
        },
        // The table/list switch; off for a page that has only this list view (the b2c cart)
        showViewToggle: {
            type: Boolean,
            default: true,
        },
        quantityDecimalPlace: {
            type: Number,
            default: () => window.bo?.lineItemMaxDecimalPlace ?? 0,
        },
    },

    data() {
        return {
            selectedIds: [],
            expandedItems: {},
            itemRemarks: {},
            isCompactLayout: false,
            hideLineItemTax: window.bo?.hideLineItemTax ?? false,
            primaryColor: window.bo_primary_color || "#594c45",
            editModalActive: false,
            editItem: null,
            editProduct: null,
            originalUom: null,
            originalQuantity: null,
            editDraft: {
                quantity: 1,
                uom: null,
                remark: "",
            },
        };
    },

    computed: {
        normalisedItems() {
            return Array.isArray(this.items) ? this.items : [];
        },
        selectedItemIds() {
            const itemIds = new Set(this.normalisedItems.map((item) => String(item?.id)));
            return this.selectedIds.filter((id) => itemIds.has(String(id)));
        },
        selectedAllItems() {
            if (!this.normalisedItems.length) return false;
            return this.selectedItemIds.length === this.normalisedItems.length;
        },
        isIndeterminate() {
            return this.selectedItemIds.length > 0 && this.selectedItemIds.length < this.normalisedItems.length;
        },
        editModalTitle() {
            const editText = this.generalTranslations.edit || "Edit";
            const objectText = this.generalTranslations.product || "Product";
            return `${editText} ${objectText}`;
        },
        editDraftTotal() {
            if (!this.editItem) return this.formatPrice(0);

            const uom = this.editDraft.uom; // This line such that the it is dependent on the uom changes
            const quantity = this.toNumber(this.editDraft.quantity);
            const unitPrice = this.getEffectiveUnitPrice(this.editItem);

            return this.formatPrice(quantity * unitPrice);
        },
    },

    watch: {
        items: {
            handler(newItems) {
                if (Array.isArray(newItems)) {
                    const nextIds = new Set(newItems.map((item) => item?.id));

                    // Keep only selections that still exist in the latest item set.
                    this.selectedIds = this.selectedIds.filter((id) => nextIds.has(id));

                    newItems.forEach((item) => {
                        if (!this.itemRemarks.hasOwnProperty(item.id)) {
                            this.itemRemarks[item.id] = item.remark || "";
                        }
                    });
                }
            },
            immediate: true,
        },
    },

    mounted() {
        this.updateCompactLayout();
        window.addEventListener("resize", this.updateCompactLayout);
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.updateCompactLayout);
    },

    methods: {
        updateCompactLayout() {
            this.isCompactLayout = window.innerWidth <= 1210; // matches breakpoint for mobile summary in CSS
        },

        onRowClick(item, event) {
            if (!this.isCompactLayout) return;

            const target = event?.target;
            if (!target || typeof target.closest !== "function") {
                this.openEditModal(item);
                return;
            }

            const interactiveSelector =
                "button, input, select, textarea, a, label, [role='button'], .bo-select, .bo-numeric-counter, .bo-cart-list__row-counter";

            if (target.closest(interactiveSelector)) return;

            this.openEditModal(item);
        },

        goToCatalog() {
            if (this.catalogUrl) window.location.href = this.catalogUrl;
        },

        selectAllItems() {
            if (this.selectedAllItems) {
                this.selectedIds = [];
            } else {
                this.selectedIds = this.normalisedItems.map((item) => item.id);
            }
        },

        emitQty(item, quantity) {
            const safeQty = Math.max(1, parseFloat(quantity) || 1);
            item.quantity = safeQty;
            // the quantity must travel with the event - listeners that re-apply it on the cart item
            // (frontend cart details) receive undefined otherwise and wipe the item quantity
            this.$emit("change_quantity", item.id, safeQty);
        },

        emitUom(item, uom) {
            this.$emit("change_uom", item.id, uom);
        },

        emitRemove(ids) {
            const selectedIds = Array.isArray(ids) ? [...ids] : [];
            this.$emit("remove_items", selectedIds);
            this.selectedIds = [];
        },

        openEditModal(item) {
            this.editItem = item;
            this.originalUom = item?.product?.selected_uom ?? null;
            this.originalQuantity = this.toNumber(item?.quantity || 1);
            this.editProduct = {
                type: item?.product?.type,
                selected_uom: item?.product?.selected_uom,
                uom: item?.product?.uom ?? [],
            };
            this.editDraft = {
                quantity: this.toNumber(item?.quantity || 1),
                uom: item?.product?.selected_uom ?? null,
                remark: item?.remark || "",
            };
            this.editModalActive = true;
        },

        closeEditModal() {
            if (this.editItem) {
                if (this.editItem.product?.type === "variable" && typeof this.editItem.product.selectUom === "function") {
                    this.editItem.product.selectUom(this.originalUom);
                }
                this.editItem.quantity = this.originalQuantity;
            }
            this.editModalActive = false;
            this.editItem = null;
            this.editProduct = null;
            this.originalUom = null;
            this.originalQuantity = null;
            this.editDraft = {
                quantity: 1,
                uom: null,
                remark: "",
            };
        },

        onEditQuantityChange(quantity) {
            const nextQuantity = Math.max(1, this.toNumber(quantity) || 1);
            this.editDraft.quantity = nextQuantity;
            if (this.editItem) {
                this.editItem.quantity = nextQuantity;
            }
        },

        onEditUomChange(uom) {
            this.editDraft.uom = uom;
            if (this.editProduct) {
                this.editProduct.selected_uom = uom;
            }
            if (this.editItem?.product?.type === "variable" && typeof this.editItem.product.selectUom === "function") {
                this.editItem.product.selectUom(uom);
            }
            this.$forceUpdate();
        },

        saveEditedItem() {
            if (!this.editItem) return;

            const item = this.editItem;
            const nextQuantity = Math.max(1, this.toNumber(this.editDraft.quantity) || 1);
            const nextRemark = this.editDraft.remark || "";
            const nextUom = this.editDraft.uom;

            if (item?.product?.type === "variable" && nextUom != null && nextUom !== this.originalUom) {
                this.$emit("change_uom", item.id, nextUom);
            }

            if (nextQuantity !== this.originalQuantity) {
                item.quantity = nextQuantity;
                this.$emit("change_quantity", item.id, nextQuantity);
            }

            if ((item.remark || "") !== nextRemark) {
                item.remark = nextRemark;
                this.$emit("update_remark", item.id, nextRemark);
            }

            this.closeEditModal();
        },

        getSku(item) {
            if (!item?.product) return "-";
            if (item.product.type === "variable") {
                return item.product.variations?.[item.product_id]?.sku || item.product.sku || "-";
            }
            return item.product.sku || "-";
        },

        resolveImage(item) {
            if (!item?.product) return this.defaultImage;

            if (item.product.type === "variable") {
                const variationImage = Object.values(item.product.variations?.[item.product_id]?.images || {})[0]
                    ?.source_url_medium;
                if (variationImage) return variationImage;
            }

            const productImage = Object.values(item.product.images || {})[0]?.source_url_medium;
            return productImage || this.defaultImage;
        },

        showStockBadge(item) {
            return this.hasInventory && !!item?.product_id;
        },

        getStockText(item) {
            const inv = this.userWarehouseInventories[item.product_id];
            if (!inv) {
                if (this.hasCapabilityRoughSellable) return this.roughStockText || "-";
                if (this.hasCapabilitySellable) return this.cartTranslations.sellable || "Sellable";
                if (this.hasCapabilityPhysical) return "0";
                return this.cartTranslations.out_of_stock || "Out of stock";
            }
            return inv.stock_balance || String(inv.balance ?? "-");
        },

        getStockClass(productId) {
            if (this.userWarehouseInventories[productId] == null) return "";

            const stockBalance = this.userWarehouseInventories[productId]?.stock_balance;
            const stockBalanceId = this.userWarehouseInventories[productId]?.stock_balance_id;
            const balance = this.userWarehouseInventories[productId]?.balance;
            const physicalBalance = this.userWarehouseInventories[productId]?.physical_balance;

            switch (true) {
                case stockBalanceId === "out_of_stock" || (typeof stockBalance === "string" && stockBalance.includes("Less than")):
                    return "bo-cart-list__stock--danger";
                case balance <= 0 && physicalBalance <= 0:
                    return "bo-cart-list__stock--danger";
                case balance <= 0 && physicalBalance > 0:
                    return "bo-cart-list__stock--warning";
                case balance > 0 && physicalBalance <= 0:
                    return "bo-cart-list__stock--warning";
                case balance > 0 && physicalBalance > 0:
                    return "bo-cart-list__stock--success";
                default:
                    return "";
            }
        },

        hasStockWarning(item) {
            return item?.id in this.rowWarningOptions;
        },

        getStockWarningText(item) {
            if (item.product?.type === "bundle") {
                return this.cartTranslations.bundle_insufficient_stocks || "Insufficient stocks, check bundle details.";
            }
            return this.cartTranslations.insufficient_stocks || "Insufficient stocks";
        },

        emitToggleView() {
            this.$emit("toggle_view");
        },

        formatPrice(price) {
            // Line items always display at 2dp, matching the DB precision - not
            // siteCurrencyDecimalPlaces, which is 0 for TWD and is reserved for the
            // cart/document-level total (shown outside this line-item list component).
            return formatPriceUtil(price, 2);
        },

        formatMetaValue(value) {
            return this.toNumber(value)
                .toFixed(2)
                .replace(/\.00$/, "")
                .replace(/(\.\d*[1-9])0$/, "$1");
        },

        getDecimalPlaces(value) {
            if (value === null || value === undefined) return 0;

            const textValue = String(value).trim();
            if (!textValue) return 0;

            const [coefficient = "", exponentText = "0"] = textValue.toLowerCase().split("e");
            const exponent = parseInt(exponentText, 10) || 0;
            const decimalPartLength = (coefficient.split(".")[1] || "").length;

            return Math.max(0, decimalPartLength - exponent);
        },

        formatPreciseMetricValue(value, decimalPlaces) {
            const metricValue = this.toNumber(value);
            const safeDecimalPlaces = Math.max(0, Math.min(8, decimalPlaces));

            if (safeDecimalPlaces === 0) {
                return String(metricValue);
            }

            const precisionThreshold = 1 / 10 ** (safeDecimalPlaces + 2);
            const normalisedValue = Math.abs(metricValue) < precisionThreshold ? 0 : metricValue;
            return normalisedValue.toFixed(safeDecimalPlaces);
        },

        getOrderDisplay(item) {
            const quantity = this.formatMetaValue(item?.quantity || 0);
            if (!item.product) return `${quantity}`;

            const selectedUom = item.product.selected_uom;
            if (!selectedUom) return `${quantity}`;

            const unitLabel = String(item.product.uom[selectedUom]?.name || "").trim();
            return `${quantity} ${unitLabel}`;
        },

        toNumber(value) {
            const parsed = parseFloat(value);
            return Number.isFinite(parsed) ? parsed : 0;
        },

        getDiscountPercent(item) {
            return this.toNumber(item?.discount_percent);
        },

        getBaseUnitPrice(item) {
            if (item?._type === "bundle_cart_item" && typeof item?.getDisplayRegularPrice === "function") {
                return this.toNumber(item.getDisplayRegularPrice());
            }

            if (typeof item?.getOriginalPrice === "function") {
                return this.toNumber(item.getOriginalPrice());
            }

            return this.toNumber(item?.unit_price ?? item?.line_item?.unit_price);
        },

        getDiscountedUnitPrice(item) {
            if (item?._type === "bundle_cart_item" && typeof item?.getDisplayUnitPrice === "function") {
                return this.toNumber(item.getDisplayUnitPrice());
            }

            if (typeof item?.getPrice === "function") {
                return this.toNumber(item.getPrice());
            }

            return this.toNumber(item?.unit_price_after_discount ?? item?.line_item?.unit_price_after_discount);
        },

        hasLineDiscount(item) {
            const baseUnitPrice = this.getBaseUnitPrice(item);
            const discountedUnitPrice = this.getDiscountedUnitPrice(item);

            if (isNaN(baseUnitPrice) || isNaN(discountedUnitPrice) || baseUnitPrice <= 0 || discountedUnitPrice < 0) {
                return false;
            }

            return discountedUnitPrice < baseUnitPrice;
        },

        getEffectiveUnitPrice(item) {
            const baseUnitPrice = this.getBaseUnitPrice(item);
            const discountedUnitPrice = this.getDiscountedUnitPrice(item);

            if (this.hasLineDiscount(item)) return discountedUnitPrice;

            return baseUnitPrice != null && !isNaN(baseUnitPrice) ? baseUnitPrice : discountedUnitPrice;
        },

        shouldShowDiscount(item) {
            return this.getDisplayDiscountPercent(item) > 0;
        },

        shouldShowOriginalPrice(item) {
            const baseUnitPrice = this.getBaseUnitPrice(item);
            const effectiveUnitPrice = this.getEffectiveUnitPrice(item);

            return baseUnitPrice > effectiveUnitPrice;
        },

        getDisplayDiscountPercent(item) {
            const baseUnitPrice = this.getBaseUnitPrice(item);
            const effectiveUnitPrice = this.getEffectiveUnitPrice(item);

            if (baseUnitPrice <= 0 || effectiveUnitPrice >= baseUnitPrice) return 0;

            return ((baseUnitPrice - effectiveUnitPrice) / baseUnitPrice) * 100;
        },

        // Each trade agreement discount level ("-RM 1.50 -2.00%"); falls back to the effective percentage for
        // items that are not cart item models or when the levels cannot be derived.
        getLineDiscountSteps(item) {
            if (
                typeof item?.getDisplayUnitPrice === "function" &&
                typeof item?.product?.getDiscountFormula === "function"
            ) {
                const steps = cartItemDiscountSteps(item, this.currencySymbol);
                if (steps.length > 0) {
                    return steps;
                }
            }

            return [`-${this.formatDiscountPercent(this.getDisplayDiscountPercent(item))}%`];
        },

        formatDiscountPercent(value) {
            const discountValue = this.toNumber(value);
            return discountValue
                .toFixed(2)
                .replace(/\.00$/, "")
                .replace(/(\.\d*[1-9])0$/, "$1");
        },

        formatPercent(value) {
            const percentValue = this.toNumber(value);
            return percentValue
                .toFixed(2)
                .replace(/\.00$/, "")
                .replace(/(\.\d*[1-9])0$/, "$1");
        },

        getTaxClass(item) {
            const selectedVariation =
                typeof item?.product?.getSelectedVariation === "function" ? item.product.getSelectedVariation() : null;
            const variationById = item?.product?.variations?.[item?.product_id] ?? null;

            return selectedVariation?.tax_class || variationById?.tax_class || item?.product?.tax_class || null;
        },

        hasTaxInfo(item) {
            return !this.taxExempted && this.getTaxClass(item) != null && !this.hideLineItemTax;
        },

        getTaxDisplay(item) {
            const taxClass = this.getTaxClass(item);
            if (!taxClass) return "";

            const taxName = taxClass.name || "";
            const taxPercent = this.formatPercent(taxClass.percentage);
            return `${taxName} ${taxPercent}%`.trim();
        },

        getItemTotal(item) {
            if (!item) return 0;
            const quantity = this.toNumber(item.quantity);
            return this.getEffectiveUnitPrice(item) * quantity;
        },

        isPointEnabled() {
            return this.pointConfig !== false && this.pointConfig != null;
        },

        calculatePoint(price) {
            if (!this.isPointEnabled()) return 0;

            const tierMultiplier = parseFloat(this.pointConfig.tier_multiplier ?? 0);
            const conversionRate = parseFloat(this.pointConfig.conversion_rate ?? 0);

            if (!Number.isFinite(tierMultiplier) || !Number.isFinite(conversionRate)) {
                return 0;
            }

            return tierMultiplier * this.toNumber(price) * conversionRate;
        },

        getLineMetric(item, getterName, fallbackKey) {
            const quantity = this.toNumber(item?.quantity || 1);

            if (typeof item?.[getterName] === "function") {
                return this.toNumber(item[getterName]()) * quantity;
            }

            return this.toNumber(item?.product?.[fallbackKey] ?? item?.[fallbackKey]) * quantity;
        },

        getLineMetricDetail(item, getterName, fallbackKey) {
            const quantityRaw = item?.quantity ?? 1;
            const metricRaw =
                typeof item?.[getterName] === "function"
                    ? item[getterName]()
                    : item?.product?.[fallbackKey] ?? item?.[fallbackKey];

            const quantity = this.toNumber(quantityRaw || 1);
            const metric = this.toNumber(metricRaw);
            const value = metric * quantity;

            // Preserve source precision (e.g. 0.0010) and quantity precision for display.
            const decimalPlaces = Math.min(8, this.getDecimalPlaces(metricRaw) + this.getDecimalPlaces(quantityRaw));

            return { value, decimalPlaces };
        },

        getWeightDisplay(item) {
            const { value, decimalPlaces } = this.getLineMetricDetail(item, "getWeight", "weight");
            if (value <= 0) return "-";

            return `${this.formatPreciseMetricValue(value, decimalPlaces)} ${this.generalTranslations.kg || "kg"}`;
        },

        getVolumeDisplay(item) {
            const { value, decimalPlaces } = this.getLineMetricDetail(item, "getVolume", "volume");
            if (value <= 0) return "-";

            return `${this.formatPreciseMetricValue(value, decimalPlaces)} ${this.generalTranslations.m3 || "m3"}`;
        },

        getDimensionalSumDisplay(item) {
            const { value, decimalPlaces } = this.getLineMetricDetail(item, "getDimensionalSum", "dimensional_sum");
            if (value <= 0) return "-";

            return `${this.formatPreciseMetricValue(value, decimalPlaces)} ${this.generalTranslations.m || "m"}`;
        },

        // free-text pack size of the line's variation (e.g. "1 piece = 5.5kg"); per unit, so not multiplied by quantity
        getPacksizeDisplay(item) {
            const selectedVariation =
                typeof item?.product?.getSelectedVariation === "function" ? item.product.getSelectedVariation() : null;
            const variationById = item?.product?.variations?.[item?.product_id] ?? null;
            const packsize = selectedVariation?.packsize ?? variationById?.packsize ?? item?.product?.packsize ?? "";

            return typeof packsize === "string" && packsize.trim() !== "" ? packsize.trim() : "-";
        },

        getSecondaryMetaEntries(item) {
            const entries = [];
            const packsizeDisplay = this.getPacksizeDisplay(item);
            const weightDisplay = this.getWeightDisplay(item);
            const dimensionalSumDisplay = this.getDimensionalSumDisplay(item);
            const volumeDisplay = this.getVolumeDisplay(item);
            const shippingClassEntries = this.getShippingClassEntries(item);

            if (packsizeDisplay !== "-") {
                entries.push(packsizeDisplay);
            }

            if (weightDisplay !== "-") {
                entries.push(`${this.generalTranslations.weight_abbreviation || "Wt."} ${weightDisplay}`);
            }

            if (dimensionalSumDisplay !== "-") {
                entries.push(
                    `${this.generalTranslations.dimensional_sum_abbreviation || "Dim."} ${dimensionalSumDisplay}`
                );
            }

            if (volumeDisplay !== "-") {
                entries.push(`${this.generalTranslations.volume_abbreviation || "Vol."} ${volumeDisplay}`);
            }

            shippingClassEntries.forEach((shippingClass) => {
                entries.push(shippingClass);
            });

            if (!shippingClassEntries.length) {
                const shippingClassDisplay = this.getShippingClassDisplay(item);
                if (shippingClassDisplay !== "-") {
                    entries.push(shippingClassDisplay);
                }
            }

            return entries;
        },

        getShippingClassEntries(item) {
            const ownShippingClass = String(this.getShippingClassNameFromLineItem(item) || "").trim();
            const childShippingClasses = this.hasComponentInfo(item)
                ? [
                      ...new Set(
                          Object.values(item?.composite_children || {})
                              .map((child) => String(this.getShippingClassNameFromLineItem(child) || "").trim())
                              .filter((value) => value && value !== "-")
                      ),
                  ]
                : [];

            const shippingClasses = [];

            if (ownShippingClass && ownShippingClass !== "-") {
                shippingClasses.push(ownShippingClass);
            }

            childShippingClasses.forEach((shippingClass) => {
                if (!shippingClasses.includes(shippingClass)) {
                    shippingClasses.push(shippingClass);
                }
            });

            return shippingClasses;
        },

        getShippingClassDisplay(item) {
            const shippingClasses = this.getShippingClassEntries(item);

            if (!shippingClasses.length) return "-";

            return shippingClasses.join(" | ");
        },

        getShippingClassNameFromLineItem(item) {
            const selectedVariation =
                typeof item?.product?.getSelectedVariation === "function" ? item.product.getSelectedVariation() : null;
            const variationById = item?.product?.variations?.[item?.product_id] ?? null;

            return (
                selectedVariation?.shipping_class?.name ||
                selectedVariation?.shipping_class ||
                variationById?.shipping_class?.name ||
                variationById?.shipping_class ||
                item?.product?.shipping_class?.name ||
                item?.product?.shipping_class ||
                ""
            );
        },

        hasComponentInfo(item) {
            const isBundle = item?.product?.type === "bundle" && item?.product?.components;
            const hasChildren = item?.composite_children && Object.keys(item.composite_children).length > 0;
            return !!(isBundle && hasChildren);
        },

        getItemLeadTime(item) {
            const selectedVariationByMethod =
                typeof item?.product?.getSelectedVariation === "function" ? item.product.getSelectedVariation() : null;
            const selectedVariationId = item?.product?.selected_variation_id || item?.product_id;
            const selectedVariationById = selectedVariationId ? item?.product?.variations?.[selectedVariationId] : null;

            const sources = [
                item?.product?.lead_time,
                selectedVariationByMethod?.lead_time,
                selectedVariationById?.lead_time,
                item?.product?.base_variation?.lead_time,
                item?.line_item?.lead_time,
                item?.line_item?.product?.lead_time,
                item?.lead_time,
            ];

            for (const value of sources) {
                if (value === null || value === undefined) continue;

                const asText = String(value).trim();
                if (asText !== "") return asText;
            }

            return "";
        },

        hasLeadTimeInfo(item) {
            return this.getItemLeadTime(item) !== "";
        },

        hasRemarkInfo(item) {
            const remark = item?.remark;
            if (remark === null || remark === undefined) return false;
            return String(remark).trim() !== "";
        },

        getLeadTimeDisplay(item) {
            const leadTime = this.getItemLeadTime(item);
            return leadTime !== "" ? leadTime : "-";
        },

        shouldShowMoreButton(item) {
            return this.hasComponentInfo(item) || this.hasLeadTimeInfo(item) || this.hasRemarkInfo(item);
        },

        toggleExpanded(itemId) {
            this.$set(this.expandedItems, itemId, !this.expandedItems[itemId]);
        },

        updateRemark(item) {
            const newRemark = this.itemRemarks[item.id];
            if (newRemark !== item.remark) {
                item.remark = newRemark;
                this.$emit("update_remark", item.id, newRemark);
            }
        },

        getComponentRows(item, parentQuantityOverride = null) {
            if (!item.product?.components || !item.composite_children) return [];

            const rows = [];

            Object.values(item.composite_children).forEach((child) => {
                if (!child) return;
                if (!child?.product) return;

                const selectedUom = child.selected_uom || child.product.selected_uom;
                const componentUomRaw =
                    child.uom?.[selectedUom]?.name ||
                    child.uom?.[selectedUom] ||
                    child.product.uom[selectedUom]?.name ||
                    child.product.uom[selectedUom] ||
                    "";

                const componentUom = String(componentUomRaw || "").trim();

                const componentQuantity = this.toNumber(child?.quantity);

                const unitPrice =
                    typeof child?.getPrice === "function"
                        ? this.toNumber(child.getPrice())
                        : this.toNumber(child?.unit_price_after_discount ?? child?.unit_price);

                rows.push({
                    name: child.product?.name || "-",
                    quantity: componentQuantity,
                    uom: componentUom,
                    unitPrice: unitPrice,
                    total: unitPrice * componentQuantity,
                });
            });
            return rows;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../../sass/variables";

.bo-cart-list {
    width: 100%;
    --toggle-border: #d9d9d9;
    --row-border: #e5e7eb;
    --muted-text: #6b7280;
    --surface-white: #{$tertiaryColor};
    --surface-ghost: #f3f4f6;
    --surface-ghost-hover: #e9ebee;
    --surface-toggle: #e3e3e3;
    --surface-point: #{$color-arclightorange-background};
    --surface-discount: #{$color-danger-background};
    --surface-danger: #ffeaea;
    --surface-danger-hover: #ffd9d6;
    --surface-no-stock: #{$color-danger-background};
    --border-light: #ddd;
    --border-subtle: #{$color-lightgray-background-label};
    --accent-info: #1f86e5;
    --accent-warning: #f4b400;
    --point-color: #f4a11a;
    --text-strong: #3b3b3b;
    --text-muted-alt: #7a7a7a;
    --text-backorder: #9e9e9e;
    --status-danger-text: #{$color-danger};
    --status-danger-border: #{$color-dangerlight};
    --status-danger-hover-border: #ff9d97;
    --status-warning-text: #b54708;
    --status-warning-border: #fffaeb;
    --status-success-text: #{$color-success};
    --status-success-border: #{$color-successlight};
    --status-action-bg: #{$color-success-background};
    --focus-ring: #{rgba($primaryColor, 0.102)};

    ::v-deep .bo-cart-list-edit-modal .modal-body {
        padding: 24px;
    }

    ::v-deep .bo-cart-list-edit-modal.modal-dialog {
        max-width: min(1440px, calc(100vw - 32px));
        width: 100%;
        margin: 16px auto;
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 16px;
        margin-bottom: 10px;
    }

    &__header {
        &-left,
        &-right {
            display: flex;
            align-items: center;
        }

        &-left-wrapper {
            display: flex;
            align-items: center;
            gap: 4px 8px;
        }
    }

    &__header-checkbox {
        align-self: center;
        margin: 0;
    }

    &__indicator {
        display: flex;
        align-items: center;
        cursor: pointer;

        i {
            font-size: 20px;
            transition: transform 0.3s ease;
            display: flex;
        }

        &.collapsed i {
            transform: rotate(180deg);
        }
    }

    @media (min-width: ($breakpoint-tablet + 1)) {
        &__indicator {
            display: none !important;
        }

        &__body.collapse {
            display: block !important;
        }
    }

    &__title-wrap {
        display: flex;
        align-items: center;
    }

    &__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 1.2;
        color: var(--toggle-primary);
    }

    &__toggle {
        display: flex;
        align-items: center;

        &-switch {
            cursor: pointer;
            position: relative;
            display: inline-block;
            width: 36px;
            height: 22px;
            margin-bottom: 0;
        }

        &-input {
            opacity: 0;
            width: 0;
            height: 0;

            &:checked + .bo-cart-list__toggle-slider::before {
                -webkit-transform: translateX(14px);
                -ms-transform: translateX(14px);
                transform: translateX(14px);
                background-color: var(--surface-white);
            }
        }

        &-slider {
            position: absolute;
            cursor: pointer;
            inset: 0;
            background-color: var(--surface-toggle);
            -webkit-transition: 0.4s;
            transition: 0.4s;

            &::before {
                position: absolute;
                content: "";
                height: 16px;
                width: 16px;
                left: 3px;
                bottom: 3px;
                background-color: var(--surface-white);
                transition: 0.4s;
            }

            &--round {
                border-radius: 34px;

                &::before {
                    border-radius: 50%;
                }
            }
        }

        &-icons {
            position: absolute;
            top: 0;
            width: 100%;
            height: 32px;
            padding-left: 6px;
            padding-right: 4px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        &-icon {
            font-size: 14px;

            &--default {
                color: var(--surface-white);
            }

            &--list {
                color: var(--toggle-primary);
            }
        }

        &--list-view {
            .bo-cart-list__toggle-switch {
                width: 60px;
                height: 32px;
            }

            .bo-cart-list__toggle-slider {
                background-color: transparent;
                border: 1px solid var(--toggle-border);
                padding: 0;

                &::before {
                    height: 30px;
                    width: 30px;
                    left: 0;
                    bottom: 0;
                    background-color: var(--toggle-primary);
                }
            }

            .bo-cart-list__toggle-input:checked + .bo-cart-list__toggle-slider {
                background-color: transparent;

                &::before {
                    border: none;
                    -webkit-transform: translateX(30px);
                    -ms-transform: translateX(30px);
                    transform: translateX(30px);
                    background-color: var(--toggle-primary);
                }
            }

            .bo-cart-list__toggle-input:checked
                + .bo-cart-list__toggle-slider
                + .bo-cart-list__toggle-icons
                .bo-cart-list__toggle-icon--list {
                color: var(--surface-white);
            }

            .bo-cart-list__toggle-input:checked
                + .bo-cart-list__toggle-slider
                + .bo-cart-list__toggle-icons
                .bo-cart-list__toggle-icon--default {
                color: var(--toggle-primary);
            }
        }

        &-input:checked + .bo-cart-list__toggle-slider {
            background-color: var(--toggle-primary);
        }
    }

    &__row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        flex-wrap: wrap;
        column-gap: 16px;
        row-gap: 0;
        // Flat list: no card border or side padding, just a divider at the bottom of each item
        margin: 0;
        padding: 12px 0;
        border-bottom: 1px solid var(--row-border);
        background: var(--surface-white);

        &--clickable {
            cursor: pointer;
        }

        // matches the table view's no-stock row
        &--no-stock {
            background: var(--surface-no-stock);
        }
    }

    &__row-left {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        min-width: 0;
        flex: 1 1 auto;

        &--has-more {
            align-items: stretch;
        }
    }

    &__checkbox {
        align-self: center;
    }

    &__meta {
        min-width: 0;
        width: 80%;
        display: flex;
        gap: 6px;
        align-items: center;
        justify-content: flex-start;
        flex-wrap: wrap;
    }

    &__image {
        width: 105px;
        height: 105px;
        object-fit: contain;
        border-radius: 8px;
        background: transparent;
        flex: 0 0 105px;
    }

    &__sku {
        font-size: 12px;
        color: var(--muted-text);
    }

    &__name {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.3;
        word-break: break-word;
    }

    &__meta-secondary {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        color: var(--muted-text);
    }

    &__meta-secondary-item {
        font-size: 12px;
        line-height: 1.3;
        white-space: nowrap;

        &--separator {
            line-height: 1;
        }
    }

    &__meta-secondary-entry {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    &__stock {
        border: 1px solid;
        color: var(--muted-text);

        &--danger {
            color: var(--status-danger-text);
            border-color: var(--status-danger-border);
        }

        &--warning {
            color: var(--status-warning-text);
            border-color: var(--status-warning-border);
        }

        &--success {
            color: var(--status-success-text);
            border-color: var(--status-success-border);
        }
    }

    &__backorder {
        color: var(--text-backorder);
        border: 1px solid var(--border-subtle);
    }

    &__stock-warning {
        color: var(--status-danger-text);
        border: 1px solid var(--status-danger-text);
        font-weight: 600;
    }

    &__stock,
    &__backorder,
    &__stock-warning {
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 12px;
    }

    &__row-right {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
        flex: 0 0 auto;

        ::v-deep .bo-select {
            width: unset !important;
        }
    }

    &__row-counter {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        flex: 0 0 auto;
        // Breathing room between the pricing and the UOM (adds to the row-right 8px gap).
        margin-left: 16px;
        // Fixed width (UOM 88px + 8px gap + quantity 120px) keeps the order/counter
        // column identical on every row, so a row with a long UOM name - or no UOM at
        // all - no longer widens it and pushes the prices beside it out of line.
        width: 216px;

        ::v-deep .bo-product-quantity-uom-counter .bo-uom-select {
            width: 88px !important;
            min-width: 88px !important;
            flex: 0 0 88px !important;
        }

        // Long unit names are clipped instead of expanding the select.
        ::v-deep .bo-product-quantity-uom-counter .bo-uom-select .bo-select__value {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        ::v-deep .bo-select {
            width: 88px !important;
        }

        ::v-deep .bo-select select {
            width: 88px;
            min-width: 88px;
        }

        ::v-deep .bo-numeric-counter {
            flex: 0 0 auto;
        }
    }

    &__icon-action {
        aspect-ratio: 1;
        padding: 5px;
        border: none;
        border-radius: 50%;
        background: var(--surface-ghost);
        color: var(--toggle-primary);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.2s ease-in-out;

        &:hover {
            background: var(--surface-ghost-hover);
        }

        i {
            font-size: 18px;
        }

        &--mobile {
            display: none;
            border-radius: 8px;
            width: 30px;
            height: 30px;
            padding: 0;

            i {
                font-size: 16px;
            }
        }

        &--mobile-under-image {
            display: none;
        }
    }

    &__basic-info {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    &__info-stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 4px;
    }

    &__price-section {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
        white-space: nowrap;

        ::v-deep .bo-point {
            color: var(--point-color);
            background-color: var(--surface-point);
            font-size: 12px;
            line-height: 1.2;
            padding: 2px 6px;
            border-radius: 4px;
            width: unset;
        }
    }

    &__point {
        &--inline-mobile {
            display: none;
            min-height: 0;
            width: auto;
            white-space: nowrap;

            ::v-deep {
                font-size: 11px;
                line-height: 1.2;
                padding: 2px 6px;
                border-radius: 4px;
                gap: 4px;
                min-height: 0;

                i::before {
                    font-size: 12px;
                    line-height: 1.2;
                }
            }
        }
    }

    &__price-head {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        width: 100%;
    }

    &__price-main {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    &__unit-price {
        display: flex;
        align-items: center;
        gap: 4px;
        font-weight: 500;
    }

    &__currency {
        font-weight: bold;

        &--discounted {
            color: var(--status-danger-text);
        }
    }

    &__price-original {
        font-size: 11px;
        text-decoration: line-through;
        color: var(--muted-text);
    }

    &__price-final {
        font-size: 14px;
        font-weight: bold;

        &--discounted {
            color: var(--status-danger-text);
        }
    }

    &__discount-row {
        display: flex;
        align-items: baseline;
        justify-content: flex-end;
        gap: 4px;
    }

    &__discount {
        font-size: 11px;
        font-weight: 600;
        color: var(--status-danger-text);
        background-color: var(--surface-discount);
        padding: 2px 6px;
        border-radius: 4px;

        // Discount levels are the same size as the price before discount (&__price-original).
        ::v-deep span {
            font-size: inherit !important;
        }
    }

    &__tax {
        font-size: 11px;
        font-weight: 500;
        border: 1px solid var(--row-border);
        padding: 2px 6px;
        border-radius: 4px;
    }

    &__total-price {
        display: flex;
        align-items: baseline;
        gap: 4px;
        font-weight: 600;
        padding: 0 8px;
        border-left: 1px solid var(--row-border);

        // min-width (not width) with right-aligned digits keeps normal line
        // totals locked in a column. An exceptionally long amount grows the box instead
        // of overflowing over the counter - only that row's column shifts, nothing overlaps
        // and no digits are clipped.
        &--row {
            flex: 0 0 auto;
            min-width: 144px;
            justify-content: flex-end;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
            border-left: none;
        }
    }

    &__qty {
        min-width: 20px;
        text-align: center;
    }

    &__mobile-summary {
        display: none;

        &-metrics {
            display: flex;
            align-items: stretch;
            margin-left: auto;
            flex-wrap: wrap;
            gap: 4px;
        }

        &-metric {
            display: grid;
            gap: 2px;
            padding-left: 10px;
            margin-left: 10px;
            border-left: 2px solid var(--row-border);

            &--order {
                border-left-color: var(--accent-info);
            }

            &--total {
                border-left-color: var(--accent-warning);
            }
        }

        &-label {
            font-size: 12px;
            color: var(--muted-text);
            line-height: 1.1;
        }

        &-value {
            font-size: 16px;
            font-weight: 600;
            line-height: 1.1;
            white-space: nowrap;
        }
    }

    &__btn {
        color: var(--status-success-text);
        border: 1px solid var(--status-success-border);
        background-color: var(--status-action-bg);
        border-radius: 6px;
        padding: 6px 10px;
        line-height: 1;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        width: max-content;

        &--more {
            background-color: var(--surface-ghost);
            border-color: var(--surface-ghost);
            color: var(--toggle-primary);
            margin-top: 10px;

            i::before {
                margin: 0;
                display: inline-block;
                transition: all 0.2s ease-in-out;
            }

            &.bo-cart-list__btn--expanded {
                background-color: var(--status-action-bg);
                border-color: var(--status-success-border);
                color: var(--status-success-text);

                i::before {
                    transform: rotate(90deg);
                }
            }
        }

        &--more-mobile {
            display: none;
        }

        &--bulk-remove {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background-color: var(--surface-danger);
            border-color: var(--status-danger-border);
            color: var(--status-danger-text);

            &:hover {
                background-color: var(--surface-danger-hover);
                border-color: var(--status-danger-hover-border);
                color: var(--status-danger-text);
            }

            i::before {
                margin: 0;
                font-size: 16px;
            }
        }
    }

    &__details {
        margin-top: 0;
        padding: 0;
        border: none;
        border-radius: 0;
        background: transparent;

        &--inline {
            flex: 1 1 100%;
            margin-top: 15px;
            margin-left: 132px;
        }

        &-title {
            font-weight: 600;
            font-size: 13px;
            margin-bottom: 8px;
            color: var(--toggle-primary);
        }

        &-label {
            display: block;
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 6px;
            color: var(--muted-text);
        }
    }

    &__details-transition-enter-active,
    &__details-transition-leave-active {
        transition: max-height 0.25s ease-in-out, opacity 0.2s ease-in-out, transform 0.25s ease-in-out;
        overflow: hidden;
    }

    &__details-transition-enter,
    &__details-transition-leave-to {
        max-height: 0;
        opacity: 0;
        transform: translateY(-8px);
    }

    &__details-transition-enter-to,
    &__details-transition-leave {
        max-height: 1000px;
        opacity: 1;
        transform: translateY(0);
    }

    &__details-section {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }

        &--components {
            margin-bottom: 0;
            padding: 12px;
            border: 1px solid var(--row-border);
            border-radius: 8px;
            background: var(--surface-white);
        }
    }

    &__details-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(300px, 420px);
        gap: 16px;
        align-items: end;

        &--single {
            grid-template-columns: minmax(0, 1fr) minmax(300px, 420px);

            .bo-cart-list__details-col--meta {
                grid-column: 2;
            }
        }
    }

    &__details-col {
        min-width: 0;

        &--meta {
            justify-self: end;
            width: 100%;
        }
    }

    &__details-meta {
        display: grid;
        gap: 10px;
        padding: 0;
        border: none;
        border-radius: 0;
        background: transparent;

        &-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
            gap: 12px;
        }

        &-label {
            font-size: 12px;
            color: var(--muted-text);
            text-align: left;
        }

        &-value {
            font-size: 12px;
            font-weight: 500;
            color: var(--toggle-primary);
            text-align: right;
            white-space: normal;
            word-break: break-word;
        }
    }

    &__bundle-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
        table-layout: fixed;

        &--edit {
            table-layout: auto;
        }

        td {
            padding: 4px 6px;
            text-align: left;
            border-bottom: none;
        }
    }

    &__bundle-col {
        &--name {
            width: auto;
        }

        &--qty {
            width: 110px;
            white-space: nowrap;
        }

        &--price {
            width: 120px;
            text-align: right !important;
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
        }
    }

    &__bundle-qty,
    &__bundle-uom {
        display: inline-block;
    }

    &__bundle-qty {
        width: 3ch;
        text-align: right;
        margin-right: 6px;
        font-variant-numeric: tabular-nums;
    }

    &__bundle-uom {
        width: 6ch;
        text-align: left;
    }

    &__remark-textarea {
        width: 100%;
        padding: 8px;
        font-size: 12px;
        border: 1px solid var(--row-border);
        border-radius: 6px;
        font-family: inherit;
        resize: vertical;
        min-height: 60px;

        &:focus {
            outline: none;
            border-color: var(--toggle-primary);
            box-shadow: 0 0 0 3px var(--focus-ring);
        }
    }

    &__input-field {
        width: 100%;
        padding: 8px;
        font-size: 12px;
        border: 1px solid var(--row-border);
        border-radius: 6px;

        &:disabled {
            background: var(--surface-ghost);
            color: var(--muted-text);
        }
    }

    &__bulk-actions {
        margin-top: 8px;
        display: flex;
        justify-content: flex-end;
    }

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 20px 16px 28px;
        border: 1px solid var(--row-border);
        border-radius: 10px;
        background: var(--surface-white);
        margin-top: 12px;

        &-image {
            width: 120px;
            max-width: 100%;
            height: auto;

            ::v-deep svg {
                width: 100%;
                height: auto;
                display: block;
            }
        }

        &-title {
            font-size: 14px;
            font-weight: 600;
            color: var(--toggle-primary);
            text-align: center;
        }
    }

    &__edit-modal {
        display: grid;
        gap: 16px;
    }

    &__edit-top {
        display: grid;
        grid-template-columns: 105px minmax(0, 1fr) auto;
        gap: 16px;
        align-items: start;
    }

    &__edit-image {
        width: 105px;
        height: 105px;
        object-fit: contain;
        border: 1px solid var(--border-light);
        border-radius: 8px;
        background: var(--surface-white);
    }

    &__edit-summary {
        width: 100%;
        min-width: 0;
        display: grid;
        gap: 6px;
    }

    // only apply to width to max-content when there are 3 or more meta entries in the first line to allow the second meta line to wrap properly, otherwise the meta section can become too wide and cause layout issues
    // 3 instead of 2 because if the sku is too short and there's not enough content in the first line, it can also cause the same issue
    // beautiful CSS only solution :DDDD
    &__meta:has(:nth-child(3)) ~ &__meta-secondary {
        width: max-content !important;
        // cap at the column width so a long weight/dimension/volume line wraps instead of running under the image
        max-width: 100%;
    }

    &__edit-components {
        grid-column: 2;
        width: 100%;
        margin-top: 4px;
        border: 1px solid var(--row-border);
        border-radius: 8px;
        background: var(--surface-white);
        padding: 10px 12px;

        &-title {
            font-size: 13px;
            font-weight: 600;
            color: var(--toggle-primary);
            margin-bottom: 6px;
        }
    }

    &__edit-price,
    &__edit-mobile-order,
    &__edit-counter,
    &__edit-total,
    &__edit-mobile-controls {
        &--mobile {
            display: none;
        }
    }

    // Default (desktop/tablet): show desktop controls, hide mobile controls.
    &__edit-counter--desktop,
    &__edit-total--desktop {
        display: flex;
    }

    &__edit-mobile-controls--mobile,
    &__edit-mobile-order--mobile,
    &__edit-counter--mobile,
    &__edit-total--mobile {
        display: none !important;
    }

    &__edit-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: nowrap;

        > * {
            flex: 0 0 auto;
        }

        ::v-deep .bo-select {
            width: auto !important;
            min-width: 96px;
        }

        ::v-deep .bo-select select {
            width: auto;
            min-width: 96px;
        }
    }

    &__edit-divider {
        height: 1px;
        background: var(--row-border);
    }

    &__edit-bottom {
        display: grid;
        grid-template-columns: minmax(220px, 1fr) minmax(260px, 1fr);
        gap: 16px;
        align-items: start;
    }

    &__edit-field {
        display: grid;
        gap: 8px;
        align-content: start;

        &--remark {
            grid-column: span 1;
        }
    }

    &__edit-label {
        font-size: 16px;
        font-weight: 600;
        color: var(--toggle-primary);

        &--mobile-order {
            margin-bottom: 2px;
        }
    }

    &__edit-input,
    &__edit-textarea {
        width: 100%;
        border: 1px solid var(--row-border);
        border-radius: 8px;
        font-size: 14px;
        color: var(--text-strong);
        background: var(--surface-white);
        padding: 12px 14px;
    }

    &__edit-input {
        height: 52px;

        &:disabled {
            background: var(--surface-ghost);
            color: var(--text-muted-alt);
        }
    }

    &__edit-textarea {
        resize: vertical;
        min-height: 100px;
    }

    &__edit-footer-total {
        display: grid;
        gap: 4px;

        &-label {
            font-size: 14px;
            font-weight: 500;
        }

        &-value {
            font-size: 16px;
            font-weight: 700;
            line-height: 1;
        }
    }

    @media (max-width: 1100px) {
        &__details-grid {
            grid-template-columns: minmax(0, 1fr);

            &--single {
                grid-template-columns: minmax(0, 1fr);

                .bo-cart-list__details-col--meta {
                    grid-column: auto;
                }
            }
        }

        &__details-col {
            &--meta {
                justify-self: stretch;
            }
        }
    }

    // Intentional magic number: 1210px is the UI break threshold where meta/price info overlap.
    @media (max-width: 1210px) {
        --mobile-info-offset: 25px;

        ::v-deep .modal-body {
            height: 450px !important;
            overflow: auto !important;
        }

        &__row {
            flex-direction: column;
            align-items: stretch;
        }

        &__row-left {
            width: 100%;
            position: relative;
            padding-left: var(--mobile-info-offset);
            align-items: flex-start;
            gap: 8px;
        }

        &__checkbox {
            order: 1;
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            margin: 0;
        }

        &__basic-info {
            order: 2;
            flex: 1 1 auto;
        }

        // Image height matches the label row + first line of the name; only those two rows reserve space for it,
        // so the weight/price lines below can use the full width
        &__image {
            position: absolute;
            top: 0;
            right: 0;
            width: 44px;
            height: 44px;
            flex: none;
        }

        &__info-stack {
            > .bo-cart-list__meta,
            > .bo-cart-list__name {
                padding-right: 52px;
            }

            > .bo-cart-list__meta {
                width: 100%;
            }
        }

        &__row-right {
            justify-content: flex-start;
            flex-wrap: wrap;
            width: 100%;
            padding-left: var(--mobile-info-offset);
        }

        &__row-counter {
            display: none;
        }

        &__price-section {
            align-items: flex-start;
            width: 100%;
            white-space: normal;

            ::v-deep .bo-point {
                font-size: 11px;
                line-height: 1.2;
                padding: 2px 6px;
            }
        }

        &__price-head {
            justify-content: flex-start;
            flex-wrap: wrap;
        }

        &__price-main {
            flex: 0 0 auto;
            max-width: 100%;
            white-space: nowrap;
        }

        &__unit-price {
            flex-wrap: nowrap;
            white-space: nowrap;
        }

        &__tax {
            white-space: nowrap;
        }

        &__point {
            &--stacked {
                display: none;
            }

            &--inline-mobile {
                display: inline-flex;
                align-items: center;
                flex: 0 0 auto;
            }
        }

        &__icon-action {
            &--desktop {
                display: none;
            }

            &--mobile {
                display: none;
            }

            &--mobile-under-image {
                position: absolute;
                right: 0;
                top: 52px;
                z-index: 1;
            }
        }

        &__btn {
            &--more-desktop {
                display: none;
            }

            &--more-mobile {
                display: inline-flex;
                margin-top: 0;
                margin-left: 0;
            }
        }

        &__mobile-summary {
            display: flex;
            align-items: center;
            gap: 12px;
            width: calc(100% - var(--mobile-info-offset));
            margin-left: var(--mobile-info-offset);
            margin-top: 8px;
        }

        &__total-price {
            &--row {
                display: none;
            }
        }

        &__details-grid {
            grid-template-columns: minmax(0, 1fr);
        }

        &__details {
            &--inline {
                margin-left: var(--mobile-info-offset);
                width: calc(100% - var(--mobile-info-offset));
            }
        }

        &__bundle-table {
            table-layout: auto;
        }

        &__bundle-table {
            tr {
                display: grid;
                grid-template-columns: minmax(0, 1fr) 96px 120px;
                column-gap: 12px;
                align-items: baseline;
            }
        }

        &__bundle-col {
            &--name {
                min-width: 0;
                overflow-wrap: anywhere;
            }

            &--qty {
                width: auto;
                white-space: nowrap;
                display: grid;
                grid-template-columns: 3ch auto;
                column-gap: 8px;
                align-items: baseline;
            }

            &--price {
                width: auto;
                white-space: nowrap;
            }
        }

        &__bundle-qty,
        &__bundle-uom {
            margin-right: 0;
        }

        &__bundle-qty {
            width: 3ch;
            text-align: right;
            font-variant-numeric: tabular-nums;
        }

        &__bundle-uom {
            width: auto;
            text-align: left;
        }

        &__edit-top {
            grid-template-columns: minmax(0, 1fr) 72px;
            grid-template-areas:
                "summary image"
                "actions actions";
            column-gap: 12px;
            row-gap: 12px;
        }

        &__edit-summary {
            grid-area: summary;
            min-width: 0;
            max-width: 100%;
        }

        &__meta {
            &--edit {
                width: 100%;
                max-width: 100%;
                min-width: 0;
                flex-wrap: wrap;
                row-gap: 6px;
                column-gap: 6px;
                align-items: center;
                justify-content: flex-start;
                align-content: flex-start;
            }
        }

        &__sku {
            &--edit {
                flex: 0 1 auto;
                min-width: 0;
                max-width: 100%;
                white-space: normal;
                overflow-wrap: anywhere;
                word-break: break-word;
            }
        }

        &__stock,
        &__backorder {
            &--edit {
                flex: 0 1 auto;
                max-width: 100%;
                white-space: nowrap;
            }
        }

        &__edit-image {
            grid-area: image;
            width: 72px;
            height: 72px;
            justify-self: end;
        }

        &__edit-actions {
            grid-area: actions;
            justify-content: flex-start;
            flex-wrap: wrap;
        }

        &__edit-price {
            &--desktop {
                display: none;
            }

            &--mobile {
                display: flex;
                align-items: flex-start;
                width: 100%;
                margin-top: 4px;
            }
        }

        &__edit-counter,
        &__edit-total {
            &--desktop {
                display: none !important;
            }

            &--mobile {
                display: flex !important;
                gap: 8px;
            }
        }

        &__edit-total--mobile {
            display: none !important;
        }

        &__edit-mobile-controls--mobile {
            display: flex !important;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            flex-wrap: nowrap;
            width: max-content;
            max-width: 100%;

            ::v-deep .bo-select,
            ::v-deep .bo-numeric-counter {
                flex: 0 0 auto;
                align-self: center;
            }

            ::v-deep .bo-select {
                width: auto !important;
            }

            ::v-deep .bo-select select {
                width: auto;
            }
        }

        &__edit-mobile-order--mobile {
            display: grid !important;
            gap: 8px;
            align-content: start;
            width: max-content;
            max-width: 100%;
        }

        &__edit-components {
            grid-column: 1 / -1;
            width: 100%;
            max-width: 100%;
        }

        &__edit-bottom {
            grid-template-columns: 1fr;
        }
    }

    // On even smaller screens, some spacing needs to be adjusted to prevent overlapping and overflow, offset remains unchanged
    @media (max-width: 560px) {
        &__bundle-table {
            font-size: 11px;
        }

        &__bundle-table {
            tr {
                grid-template-columns: minmax(0, 1fr) 80px 98px;
                column-gap: 6px;
                align-items: baseline;
            }
        }

        &__bundle-col {
            &--name {
                overflow-wrap: break-word;
                word-break: break-word;
            }

            &--qty {
                display: grid;
                grid-template-columns: 3ch auto;
                column-gap: 6px;
                align-items: baseline;
                white-space: nowrap;
            }

            &--price {
                white-space: nowrap;
                text-align: right !important;
            }
        }
    }

    @media (max-width: 430px) {
        &__edit-summary {
            max-width: 100%;
        }

        &__meta {
            &--edit {
                width: 80%;
                min-width: 0;
                flex-wrap: wrap;
                column-gap: 6px;
                row-gap: 6px;
            }
        }

        &__sku {
            &--edit {
                flex: 0 1 auto;
                max-width: 100%;
                white-space: normal;
                overflow-wrap: anywhere;
                word-break: break-word;
            }
        }

        &__stock,
        &__backorder {
            &--edit {
                flex: 0 1 auto;
                min-width: 0;
                max-width: 100%;
            }
        }
    }

    @media (max-width: 380px) {
        &__bundle-table {
            tr {
                grid-template-columns: minmax(0, 1fr) auto;
                grid-template-areas:
                    "name name"
                    "qty price";
                row-gap: 4px;
                column-gap: 8px;
                align-items: start;
            }
        }

        &__bundle-col {
            &--name {
                grid-area: name;
            }

            &--qty {
                grid-area: qty;
                justify-self: start;
            }

            &--price {
                grid-area: price;
                justify-self: end;
            }
        }
    }
}
</style>
